/**
 * Order Management — expanded row detail.
 *
 * Layout (matches wireframe):
 *  ┌──────────────────┬──────────────────┬──────────────────┐
 *  │                  │  Order Details   │  Driver Details  │
 *  │   Order List     ├──────────────────┤──────────────────┤
 *  │                  │ Customer Details │                  │
 *  │                  ├──────────────────┤  Order Status    │
 *  │                  │ Restaurant Info  │                  │
 *  ├──────────────────┼──────────────────┼──────────────────┤
 *  │  Status Update   │ Dedicated Drivers│      Map         │
 *  └──────────────────┴──────────────────┴──────────────────┘
 */

import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import DirectionsBikeOutlinedIcon from '@mui/icons-material/DirectionsBikeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

import { ContactActionModal, buildOrderContact } from './ContactActionModal';
import { BRANCH_STATUS_OPTIONS } from './orderManagementConfig';
import {
  cardSx,
  cardHeaderSx,
  cardHeaderTitleSx,
  highlightedCardSx,
  infoCardBodySx,
  nestedScrollSx,
  ROW2_CARD_MIN_HEIGHT,
  BOTTOM_ROW_GRID_COLUMNS,
  TH_SX,
  TD_SX,
} from './orderManagementStyles';
import {
  buildOrderTimeline,
  fmtDate,
  fmtDateTime,
  fmtTime,
  formatStatusLabel,
  getStatusStyle,
  normalizeFreeItem,
  normalizeMealItems,
  normalizeRegularItems,
  normalizeUpsellItems,
  formatDedicatedDriverAccountType,
  getDedicatedDriverAccountTypeStyle,
} from './orderManagementOrderHelpers';
import { ClampedText, ContactHeaderIconButtons, getContactIconButtonSx, ItemsSection } from './OrderManagementShared';

// ── Shared sx tokens used across the accordion ───────────────────────────────

const buildCardSx = (colors) => ({
  ...cardSx,
  borderRadius: '14px',
  bgcolor: colors.white,
  border: `1px solid ${colors.border}`,
  boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
  overflow: 'hidden',
});

const getDedicatedDriverPhone = (d) =>
  d?.phone_no ?? d?.phone ?? d?.driver_phone_no ?? null;

export function OrderDetailAccordion({ order, colors }) {
  const CARD_SX = buildCardSx(colors);
  // ── Item normalization ──────────────────────────────────────────────────────
  const mealItems    = normalizeMealItems(order?.meal_item_names);
  const regularItems = normalizeRegularItems(order?.item_names);
  const upsellItems  = normalizeUpsellItems(order?.upsale_item_names);
  const freeItems    = normalizeFreeItem(order);
  const timeline     = buildOrderTimeline(order);
  const hasItems     = mealItems.length > 0 || regularItems.length > 0 || upsellItems.length > 0 || freeItems.length > 0;

  const driver    = order?.driver_information;
  const hasDriver = driver?.email != null || driver?.first_name != null;

  const dedicatedDrivers = Array.isArray(order?.dedicated_drivers) ? order.dedicated_drivers : [];

  // ── Contact modal state ─────────────────────────────────────────────────────
  const [contactModal, setContactModal] = useState({ open: false, type: 'call', contact: null });

  const openContactModal = (who, type) => {
    setContactModal({ open: true, type, contact: buildOrderContact(order, who, driver) });
  };

  const closeContactModal = () => setContactModal((prev) => ({ ...prev, open: false }));

  const handleContactSubmit = async (payload) => {
    if (payload.type === 'call' && payload.contact?.phone) {
      const digits = String(payload.contact.phone).replace(/[^\d+]/g, '');
      if (digits) window.open(`tel:${digits}`, '_self');
    }
    await new Promise((r) => setTimeout(r, 400));
  };

  const currentStatus = String(order?.internal_order_status ?? '').toLowerCase().trim();
  const currentOption = BRANCH_STATUS_OPTIONS.find((o) => o.value.toLowerCase() === currentStatus);

  const handleDedicatedDriverCall = (d) => {
    const phone = getDedicatedDriverPhone(d);
    if (!phone) return;
    const digits = String(phone).replace(/[^\d+]/g, '');
    if (digits) window.open(`tel:${digits}`, '_self');
  };

  // ── Center column height measurement (drives left/right column heights) ──────
  const centerRef = useRef(null);
  const [rowHeight, setRowHeight] = useState(null);

  useEffect(() => {
    const el = centerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setRowHeight(Math.round(entry.contentRect.height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const orderStatusScrollRef = useRef(null);
  const currentStatusItemRef = useRef(null);

  useEffect(() => {
    const scrollEl = orderStatusScrollRef.current;
    const currentEl = currentStatusItemRef.current;
    if (!scrollEl || !currentEl) return undefined;

    const scrollToCurrentStatus = () => {
      if (scrollEl.scrollHeight <= scrollEl.clientHeight + 1) return;
      currentEl.scrollIntoView({ block: 'center', inline: 'nearest' });
    };

    scrollToCurrentStatus();
    const frame = requestAnimationFrame(scrollToCurrentStatus);

    const ro = new ResizeObserver(scrollToCurrentStatus);
    ro.observe(scrollEl);

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [timeline, order?._id, order?.internal_order_status, rowHeight, hasDriver]);

  const driverCarryItems = upsellItems.length > 0 || freeItems.length > 0;

  // ── Reusable helpers ────────────────────────────────────────────────────────
  const infoRow = (label, value, { capitalize = true } = {}) => {
    if (value == null || String(value).trim() === '') return null;
    return (
      <Box key={label} sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'flex-start', minWidth: 0 }}>
        <Box sx={{ width: 84, flexShrink: 0 }}>
          <ClampedText text={label} sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11.5, color: '#9CA3AF', lineHeight: 1.4 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ClampedText text={String(value)} capitalize={capitalize} sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12.5, color: '#374151', lineHeight: 1.4 }} />
        </Box>
      </Box>
    );
  };

  const infoProfileHeader = ({ name, subtitle, userId, imageSrc, FallbackIcon, avatarSize = 48 }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', width: '100%', minWidth: 0, mb: 0 }}>
      <Box
        sx={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: '50%',
          bgcolor: colors.headerBg,
          border: `1.5px solid ${colors.border}`,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {imageSrc
          ? <Box component='img' src={imageSrc} alt='' sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <FallbackIcon sx={{ fontSize: avatarSize * 0.45, color: '#9CA3AF' }} />}
      </Box>
      <Box sx={{ width: '100%', minWidth: 0, mt: 1.25, px: 0.5 }}>
        <ClampedText
          capitalize
          text={name}
          sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 14, fontWeight: 700, color: '#111827', display: 'block' }}
        />
        {userId != null && String(userId).trim() !== '' && (
          <ClampedText
            text={String(userId)}
            sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11.5, color: '#6B7280', mt: 0.35, display: 'block' }}
          />
        )}
        {subtitle && (
          <ClampedText
            capitalize
            text={subtitle}
            sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11.5, color: '#9CA3AF', mt: 0.35, display: 'block' }}
          />
        )}
      </Box>
      <Divider sx={{ width: '100%', mt: 1.5, mb: 0.25, borderColor: colors.border }} />
    </Box>
  );

  const contactHeaderActions = (who) => (
    <ContactHeaderIconButtons
      who={who}
      colors={colors}
      onAction={(type) => openContactModal(who, type)}
    />
  );

  const CardHeader = ({ Icon, title, right }) => (
    <Box sx={{
      ...cardHeaderSx,
      minWidth: 0,
      bgcolor: colors.cardHeaderBg ?? 'transparent',
      borderBottom: `1px solid ${colors.cardHeaderBorder ?? colors.border}`,
    }}>
      <Icon sx={{ fontSize: 16, color: colors.ink, flexShrink: 0, mt: 0.15 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ClampedText text={title} sx={{ ...cardHeaderTitleSx, display: 'block' }} />
      </Box>
      {right && (
        <Box sx={{ ml: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
          {right}
        </Box>
      )}
    </Box>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: colors.accordionBg ?? colors.pageBg ?? '#F5F7FB', borderTop: `1px solid ${colors.border}`, p: { xs: 1.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}>

      {/* ═══════════════════════════════════════════════════════════════════════
          TOP ROW — 3 columns:
          [Order List (~520px)] [Middle stack: 3 cards] [Right stack: 2 cards]
      ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 2,
          alignItems: 'flex-start',
        }}
      >

        {/* ── LEFT: height locked to center column; line items scroll inside ── */}
        <Box
          sx={{
            width: { xs: '100%', md: 520 },
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            ...(rowHeight != null && { height: rowHeight }),
          }}
        >
        <Box sx={{ ...CARD_SX, ...highlightedCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            Icon={ReceiptLongOutlinedIcon}
            title='Order List'
            right={
              <Box sx={{ bgcolor: colors.orderBadgeBg ?? '#F3F4F6', borderRadius: '8px', px: 1.25, py: 0.35, maxWidth: 200 }}>
                <ClampedText
                  text={order?.order_no ?? '—'}
                  sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 10.5, fontWeight: 600, color: colors.orderBadgeText ?? '#374151', display: 'block' }}
                />
              </Box>
            }
          />

          {/* Scrollable items */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              borderBottom: hasItems ? `1px solid ${colors.border}` : 'none',
              '&::-webkit-scrollbar': { width: 5 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#D1D5DB', borderRadius: 3 },
              '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
            }}
          >
            {hasItems ? (
              <Table size='small' sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col />
                  <col style={{ width: 40 }} />
                  <col style={{ width: 108 }} />
                  <col style={{ width: 108 }} />
                </colgroup>
                <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <TableRow>
                    {['Item', 'Qty', 'Price', 'Total'].map((col, i) => (
                      <TableCell
                        key={col}
                        align={i >= 2 ? 'right' : i === 1 ? 'center' : 'left'}
                        sx={{
                          ...TH_SX,
                          py: 1,
                          fontSize: 10.5,
                          border: 'none',
                          borderBottom: `1px solid ${colors.border}`,
                          bgcolor: colors.headerBg,
                          ...(i === 1 && { width: 40, minWidth: 40, maxWidth: 40 }),
                          ...(i >= 2 && { width: 108, minWidth: 108, maxWidth: 108 }),
                          ...(i === 0 && { minWidth: 0, maxWidth: 0, overflow: 'hidden' }),
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <ItemsSection items={mealItems}    label='Combo Meals'  chipBg='#EDE9FE' chipColor='#5B21B6' />
                  <ItemsSection items={regularItems} label='Regular Menu' chipBg='#DBEAFE' chipColor='#1E40AF' />
                  <ItemsSection items={upsellItems}  label='Upsell'       chipBg='#D1FAE5' chipColor='#065F46' />
                  <ItemsSection items={freeItems}    label='Standard Delivery Free Item'    chipBg='#F0FDF4' chipColor='#166534' isFreeSection />
                </TableBody>
              </Table>
            ) : (
              <Box sx={{ height: '100%', minHeight: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <ShoppingBagOutlinedIcon sx={{ fontSize: 28, color: '#D1D5DB', mb: 1 }} />
                <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#9CA3AF' }}>
                  No items recorded
                </Typography>
              </Box>
            )}
          </Box>

          {/* Financials */}
          <Box sx={{ px: 2, py: 1.75, borderTop: `1px solid ${colors.border}`, bgcolor: colors.headerBg, flexShrink: 0 }}>
            {[
              { label: 'Subtotal', value: order?.sub_total },
              ...(Number(order?.upsale_amount) > 0 ? [{ label: 'Upsell', value: order.upsale_amount }] : []),
              ...(Number(order?.oneTimeMeal_amount) > 0
                ? [{ label: `Meal Plan${order?.oneTimeMeal_persons ? ` (${order.oneTimeMeal_persons})` : ''}`, value: order.oneTimeMeal_amount }]
                : []),
              ...(Number(order?.delivery_price) > 0
                ? [{ label: `Delivery · ${order?.delivery_option_type && order.delivery_option_type !== 'none' ? String(order.delivery_option_type).replace(/_/g, ' ') : ''}`, value: order.delivery_price }]
                : []),
              { label: 'Tax', value: order?.tax },
              ...(Number(order?.tip) > 0 ? [{ label: 'Tip', value: order.tip }] : []),
            ]
              .filter((r) => r.value != null)
              .map(({ label, value }) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                  <ClampedText text={label} sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#6B7280', flex: 1, display: 'block' }} />
                  <ClampedText text={`$${Number(value).toFixed(2)}`} sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#374151', flexShrink: 0, maxWidth: '42%', display: 'block', textAlign: 'right' }} />
                </Box>
              ))}
            <Divider sx={{ my: 1, borderColor: colors.border }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 12.5, fontWeight: 700, color: '#374151' }}>
                Total Charged
              </Typography>
              <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 19, fontWeight: 700, color: '#111827' }}>
                ${Number(order?.total_charged ?? 0).toFixed(2)}
              </Typography>
            </Box>
            {order?.refunded && (
              <Box sx={{ mt: 1 }}>
                <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: '#FEE2E2', color: '#991B1B', px: 1.25, py: 0.4, borderRadius: '20px', fontSize: 11.5, fontFamily: 'Inter Bold, sans-serif', fontWeight: 600 }}>
                  Refunded
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        </Box>

        {/* ── CENTER: natural height — drives left & right column heights ──── */}
        <Box ref={centerRef} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* ORDER DETAILS */}
          <Box sx={{ ...CARD_SX }}>
            <CardHeader Icon={InfoOutlinedIcon} title='Order Details' />
            <Box sx={infoCardBodySx}>
              {infoRow('Order #', order?.order_no, { capitalize: false })}
              {infoRow('Delivery date', order?.delivery_date, { capitalize: false })}
              {infoRow('Time slot', order?.delivery_time_slot, { capitalize: false })}
              {infoRow('Delivery address', order?.delivery_address, { capitalize: false })}
              {infoRow(
                'Delivery option',
                order?.delivery_option_type && order.delivery_option_type !== 'none'
                  ? String(order.delivery_option_type).replace(/_/g, ' ')
                  : null,
                { capitalize: false }
              )}
              {infoRow(
                'Delivered by',
                order?.delivered_by && order.delivered_by !== 'none' ? order.delivered_by : null,
                { capitalize: false }
              )}
              {infoRow('Created', fmtDateTime(order?.create_at), { capitalize: false })}
            </Box>
          </Box>

          {/* CUSTOMER DETAILS */}
          <Box sx={{ ...CARD_SX }}>
            <CardHeader
              Icon={PersonOutlinedIcon}
              title='Customer Details'
              right={contactHeaderActions('customer')}
            />
            <Box sx={infoCardBodySx}>
              {infoProfileHeader({
                name: `${order?.cust_firstName ?? ''} ${order?.cust_lastName ?? ''}`.trim() || '—',
                imageSrc: order?.customer_profile_picture ?? order?.cust_profile_picture ?? null,
                FallbackIcon: PersonOutlinedIcon,
              })}
              {infoRow('Phone', order?.customer_phone_no, { capitalize: false })}
              {infoRow('Address', order?.customer_geolocation?.formattedAddress ?? order?.delivery_address, { capitalize: false })}
            </Box>
          </Box>

          {/* RESTAURANT INFO — natural height; center column ends here */}
          <Box sx={{ ...CARD_SX }}>
            <CardHeader
              Icon={StorefrontOutlinedIcon}
              title='Restaurant Info'
              right={contactHeaderActions('kitchen')}
            />
            <Box sx={infoCardBodySx}>
              {infoRow('Kitchen', order?.kitchen_name ?? order?.kitchen_managingBranchName)}
              {infoRow('Address', order?.kitchen_geolocation?.formattedAddress ?? order?.pickup_kitchen_address, { capitalize: false })}
            </Box>
          </Box>
        </Box>

        {/* ── RIGHT: height locked to center column; Order Status scrolls inside ── */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0, ...(rowHeight != null && { height: rowHeight }) }}>

          {/* DRIVER DETAILS — always visible */}
          <Box sx={{ ...CARD_SX, flexShrink: 0 }}>
            <CardHeader
              Icon={DirectionsBikeOutlinedIcon}
              title='Driver Details'
              right={hasDriver ? contactHeaderActions('driver') : null}
            />
            {hasDriver ? (
              <Box sx={infoCardBodySx}>
                {infoProfileHeader({
                  name: `${driver?.first_name ?? ''} ${driver?.last_name ?? ''}`.trim() || '—',
                  userId: driver?.user_id,
                  subtitle: driver?.driver_type ? String(driver.driver_type).replace(/_/g, ' ') : null,
                  imageSrc: driver?.driver_profile_picture ?? null,
                  FallbackIcon: DirectionsBikeOutlinedIcon,
                })}
                {/* {infoRow('Accepted', fmtDateTime(driver?.order_accepted_time), { capitalize: false })}
                {infoRow('Picked up', fmtDateTime(driver?.order_picked_time), { capitalize: false })} */}
                {infoRow('Delivered', fmtDateTime(driver?.order_delivered_time), { capitalize: false })}
                {infoRow('Duration', driver?.timeTookToDeliver, { capitalize: false })}
                {driver?.otp_driver != null && infoRow('OTP', String(driver.otp_driver), { capitalize: false })}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, py: 3, px: 2 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: colors.headerBg, border: `1.5px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DirectionsBikeOutlinedIcon sx={{ fontSize: 22, color: '#D1D5DB' }} />
                </Box>
                <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 13, fontWeight: 600, color: '#6B7280' }}>
                  Driver Not Assigned
                </Typography>
                <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11.5, color: '#9CA3AF', textAlign: 'center', lineHeight: 1.45 }}>
                  No driver has been assigned to this order yet
                </Typography>
              </Box>
            )}
          </Box>

          {/* ORDER STATUS — grows to fill column (full height when no driver) */}
          <Box sx={{ ...CARD_SX, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <CardHeader Icon={LocalShippingOutlinedIcon} title='Order Status' />
            <Box
              ref={orderStatusScrollRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                px: 2.5,
                py: 2,
                '&::-webkit-scrollbar': { width: 5 },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#D1D5DB', borderRadius: 3 },
                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
              }}
            >
              {timeline.map(({ status, timestamp, isComplete, isCurrent, isPending }, idx) => {
                const isLast = idx === timeline.length - 1;
                const { bg, color } = getStatusStyle(status);
                const showTimestamp = isComplete && timestamp;
                const connectorColor = isPending ? '#E5E7EB' : '#D1D5DB';
                const leftPlaceholder = isCurrent && !showTimestamp
                  ? { line1: 'In progress', line2: 'Now' }
                  : isPending
                    ? { line1: 'Upcoming', line2: null }
                    : null;
                return (
                  <Box
                    key={`${status}-${idx}`}
                    ref={isCurrent ? currentStatusItemRef : null}
                    sx={{ display: 'flex', alignItems: 'flex-start' }}
                  >
                    {/* Timestamp or placeholder — keeps status label aligned */}
                    <Box sx={{ width: 72, flexShrink: 0, textAlign: 'right', pr: 1.5, pt: 0.4, pb: isLast ? 0 : 1.75 }}>
                      {showTimestamp && (
                        <>
                          <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 10.5, color: '#9CA3AF', lineHeight: 1.4 }}>
                            {fmtDate(timestamp)}
                          </Typography>
                          <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 11, fontWeight: 700, color: '#6B7280', lineHeight: 1.4 }}>
                            {fmtTime(timestamp)}
                          </Typography>
                        </>
                      )}
                      {!showTimestamp && leftPlaceholder && (
                        <>
                          <Typography
                            sx={{
                              fontFamily: isCurrent ? 'Inter Bold, sans-serif' : 'Inter Regular, sans-serif',
                              fontSize: 10.5,
                              fontWeight: isCurrent ? 700 : 400,
                              color: isCurrent ? '#6B7280' : '#9CA3AF',
                              lineHeight: 1.4,
                            }}
                          >
                            {leftPlaceholder.line1}
                          </Typography>
                          {leftPlaceholder.line2 && (
                            <Typography
                              sx={{
                                fontFamily: 'Inter Bold, sans-serif',
                                fontSize: 11,
                                fontWeight: 700,
                                color: isCurrent ? colors.ink ?? '#111111' : '#9CA3AF',
                                lineHeight: 1.4,
                              }}
                            >
                              {leftPlaceholder.line2}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                    {/* Dot + connector */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 22, flexShrink: 0 }}>
                      {isCurrent ? (
                        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: bg, border: `2px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
                        </Box>
                      ) : isComplete ? (
                        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: colors.completedDot ?? '#111111', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <CheckCircleRoundedIcon sx={{ fontSize: 14, color: colors.completedDotText ?? '#FFFFFF' }} />
                        </Box>
                      ) : (
                        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#F9FAFB', border: '1.5px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
                      )}
                      {!isLast && (
                        <Box
                          sx={{
                            flex: 1,
                            minHeight: 24,
                            width: 2,
                            backgroundImage: `repeating-linear-gradient(to bottom, ${connectorColor} 0, ${connectorColor} 5px, transparent 5px, transparent 10px)`,
                            mt: 0.5,
                          }}
                        />
                      )}
                    </Box>
                    {/* Label */}
                    <Box sx={{ flex: 1, minWidth: 0, pl: 1.5, pt: 0.5, pb: isLast ? 0 : 1.75 }}>
                      <ClampedText
                        text={formatStatusLabel(status)}
                        sx={{
                          fontFamily: isCurrent ? 'Inter Bold, sans-serif' : 'Inter Regular, sans-serif',
                          fontSize: 13.5,
                          fontWeight: isCurrent ? 700 : 400,
                          color: isPending ? '#9CA3AF' : isCurrent ? '#111111' : '#374151',
                          lineHeight: 1.3,
                          display: 'block',
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          BOTTOM ROW — narrow Status | wide Dedicated Drivers | narrower Map
      ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: BOTTOM_ROW_GRID_COLUMNS,
          gap: 2,
          alignItems: 'stretch',
        }}
      >

        {/* ── STATUS UPDATE — coming soon ──────────────────────────────────── */}
        <Box sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', minHeight: ROW2_CARD_MIN_HEIGHT, maxHeight: ROW2_CARD_MIN_HEIGHT }}>
          <CardHeader
            Icon={TuneRoundedIcon}
            title='Status Update'
            right={
              currentOption ? (
                <Chip
                  label={currentOption.label}
                  size='small'
                  sx={{
                    height: 20,
                    fontSize: 10.5,
                    fontFamily: 'Inter Bold, sans-serif',
                    fontWeight: 700,
                    bgcolor: currentOption.bg,
                    color: currentOption.color,
                    borderRadius: '6px',
                    border: `1px solid ${currentOption.color}22`,
                    '& .MuiChip-label': { px: 1, py: 0 },
                  }}
                />
              ) : null
            }
          />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              p: 2,
              bgcolor: colors.headerBg,
            }}
          >
            <TuneRoundedIcon sx={{ fontSize: 32, color: colors.muted, opacity: 0.5 }} />
            <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 15, color: colors.ink, letterSpacing: '0.04em' }}>
              Coming Soon
            </Typography>
            <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11.5, color: colors.muted, textAlign: 'center', maxWidth: 160, lineHeight: 1.45 }}>
              Order status updates will be available here
            </Typography>
          </Box>
        </Box>

        {/* ── DEDICATED DRIVER LIST ─────────────────────────────────────────── */}
        <Box sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', minHeight: ROW2_CARD_MIN_HEIGHT, maxHeight: ROW2_CARD_MIN_HEIGHT }}>
          <CardHeader
            Icon={DirectionsBikeOutlinedIcon}
            title={
              dedicatedDrivers.length > 0
                ? `Dedicated Drivers (${dedicatedDrivers.length})`
                : 'Dedicated Drivers'
            }
          />

          {dedicatedDrivers.length === 0 ? (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2, gap: 1 }}>
              <DirectionsBikeOutlinedIcon sx={{ fontSize: 28, color: '#D1D5DB' }} />
              <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#9CA3AF' }}>
                No dedicated drivers assigned
              </Typography>
            </Box>
          ) : (
            <Box sx={nestedScrollSx}>
              <Table size='small' sx={{ width: '100%', minWidth: 480, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '26%' }} />
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <TableRow>
                    {['Driver ID', 'Manager', 'Account type', 'Status', 'Call'].map((col, i) => (
                      <TableCell
                        key={col}
                        align={col === 'Call' ? 'center' : 'left'}
                        sx={{ ...TH_SX, py: 1, fontSize: 10.5, px: i === 0 ? 2.5 : 2, border: 'none', borderBottom: `1px solid ${colors.border}`, bgcolor: colors.headerBg }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dedicatedDrivers.map((d, idx) => {
                    const isLast = idx === dedicatedDrivers.length - 1;
                    const bdr = isLast ? 'none' : `1px solid ${colors.rowDivider}`;
                    const clampCellSx = { overflow: 'hidden', maxWidth: 0, minWidth: 0 };
                    const accountLabel = formatDedicatedDriverAccountType(d.account_type);
                    const accountStyle = getDedicatedDriverAccountTypeStyle(d.account_type);
                    const driverPhone = getDedicatedDriverPhone(d);
                    const canCall = Boolean(driverPhone && String(driverPhone).replace(/[^\d+]/g, ''));
                    return (
                      <TableRow key={d._id || idx} sx={{ '&:hover': { bgcolor: '#F9FAFB' } }}>
                        <TableCell sx={{ ...TD_SX, py: 1.25, pl: 2.5, border: 'none', borderBottom: bdr, ...clampCellSx }}>
                          <ClampedText text={d.driverId ?? '—'} sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 12.5, fontWeight: 600, color: '#111827', display: 'block' }} />
                        </TableCell>
                        <TableCell sx={{ ...TD_SX, py: 1.25, border: 'none', borderBottom: bdr, fontSize: 12, ...clampCellSx }}>
                          <ClampedText text={d.manager_user_id ?? '—'} sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: colors.ink, display: 'block' }} />
                        </TableCell>
                        <TableCell
                          sx={{
                            ...TD_SX,
                            py: 1.25,
                            px: 2,
                            border: 'none',
                            borderBottom: bdr,
                            verticalAlign: 'middle',
                            minWidth: 120,
                          }}
                        >
                          <Tooltip title={accountLabel} placement='top' arrow>
                            <Box
                              component='span'
                              sx={{
                                display: 'inline-block',
                                maxWidth: '100%',
                                fontSize: 11,
                                fontFamily: 'Inter Regular, sans-serif',
                                fontWeight: 500,
                                bgcolor: accountStyle.bg,
                                color: accountStyle.color,
                                borderRadius: '20px',
                                px: 1,
                                py: 0.35,
                                lineHeight: 1.35,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                verticalAlign: 'middle',
                                boxSizing: 'border-box',
                              }}
                            >
                              {accountLabel}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ ...TD_SX, py: 1.25, border: 'none', borderBottom: bdr }}>
                          <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: 11.5, fontFamily: 'Inter Bold, sans-serif', fontWeight: 600, bgcolor: d.status ? '#D1FAE5' : '#F3F4F6', color: d.status ? '#065F46' : '#9CA3AF', borderRadius: '20px', px: 1, py: 0.3 }}>
                            {d.status ? 'Active' : 'Off'}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...TD_SX, py: 1, border: 'none', borderBottom: bdr, textAlign: 'center', width: 52, minWidth: 52, maxWidth: 52 }}>
                          <Tooltip title={canCall ? `Call ${d.driverId ?? 'driver'}` : 'No phone on file'} placement='top' arrow>
                            <span>
                              <IconButton
                                size='small'
                                aria-label={`Call driver ${d.driverId ?? idx}`}
                                disabled={!canCall}
                                onClick={() => handleDedicatedDriverCall(d)}
                                sx={getContactIconButtonSx(colors)}
                              >
                                <PhoneOutlinedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>

        {/* ── DRIVER CARRY ITEMS (map card slot) ─────────────────────────────── */}
        <Box sx={{ ...CARD_SX, ...highlightedCardSx, display: 'flex', flexDirection: 'column', minHeight: ROW2_CARD_MIN_HEIGHT, maxHeight: ROW2_CARD_MIN_HEIGHT, overflow: 'hidden' }}>
          <CardHeader
            Icon={LocalShippingOutlinedIcon}
            title='Driver must carry these items during delivery'
          />

          {!driverCarryItems ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2.5,
                gap: 1.25,
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              <ShoppingBagOutlinedIcon sx={{ fontSize: 28, color: '#6B7280', flexShrink: 0 }} />
              <Typography
                sx={{
                  fontFamily: 'Inter Regular, sans-serif',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'center',
                  lineHeight: 1.5,
                  width: '100%',
                  maxWidth: '100%',
                  px: 0.5,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                No upsell or standard delivery free items for this order
              </Typography>
            </Box>
          ) : (
            <Box sx={{ ...nestedScrollSx, px: 2.5, py: 1.5 }}>
              {[
                { items: upsellItems, label: 'Upsell', chipBg: '#D1FAE5', chipColor: '#065F46', isFreeSection: false },
                { items: freeItems, label: 'Standard Delivery Free Item', chipBg: '#F0FDF4', chipColor: '#166534', isFreeSection: true },
              ]
                .filter(({ items }) => items.length > 0)
                .map(({ items, label, chipBg, chipColor, isFreeSection }, sectionIdx, sections) => (
                  <Box key={label} sx={{ mb: sectionIdx < sections.length - 1 ? 1.5 : 0 }}>
                    <Box
                      component='span'
                      sx={{
                        display: 'inline-block',
                        fontSize: 10,
                        fontFamily: 'Inter Bold, sans-serif',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: chipColor,
                        bgcolor: chipBg,
                        px: 1,
                        py: 0.4,
                        borderRadius: '5px',
                        mb: 1,
                      }}
                    >
                      {label}
                    </Box>
                    {items.map((item, idx) => {
                      const isLast = idx === items.length - 1;
                      return (
                        <Box
                          key={`${label}-${idx}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.25,
                            py: 1,
                            borderBottom: isLast ? 'none' : `1px solid ${colors.rowDivider ?? colors.border}`,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '10px',
                              bgcolor: '#F3F4F6',
                              border: `1px solid ${colors.border}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <ShoppingBagOutlinedIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <ClampedText
                              text={item.name}
                              capitalize
                              sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 13, fontWeight: 600, color: '#111827' }}
                            />
                            {item.notes && (
                              <ClampedText
                                text={item.notes}
                                capitalize
                                sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11, color: '#9CA3AF', mt: 0.1 }}
                              />
                            )}
                          </Box>
                          <Chip
                            label={`×${item.qty ?? 1}`}
                            size='small'
                            sx={{
                              height: 22,
                              fontFamily: 'Inter Bold, sans-serif',
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor: isFreeSection || item.isFree ? '#F0FDF4' : '#F3F4F6',
                              color: isFreeSection || item.isFree ? '#166534' : '#374151',
                              border: `1px solid ${isFreeSection || item.isFree ? '#BBF7D0' : colors.border}`,
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                ))}
            </Box>
          )}
        </Box>
      </Box>

      <ContactActionModal
        open={contactModal.open}
        type={contactModal.type}
        contact={contactModal.contact}
        onClose={closeContactModal}
        onSubmit={handleContactSubmit}
      />
    </Box>
  );
}
